import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, Check, Loader2, CreditCard, RefreshCw, FileText, ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { track } from '@/lib/analytics';
import { formatMoney } from '@/lib/money';
import { useI18n } from '@/lib/i18n';
import { useMe } from '@/lib/auth';
import { AddonStore, CART_EMPTY, type Cart, type Entitlements } from '@/components/billing/AddonStore';
import { SavedCards, type PaymentMethod } from '@/components/billing/SavedCards';
import { InvoiceList, type Invoice } from '@/components/billing/InvoiceList';
import { CheckoutDialog, type CheckoutTarget } from '@/components/billing/CheckoutDialog';
import { FreeQuotaGauge } from '@/components/FreeQuotaGauge';

// Cobrança v2 — checkout TRANSPARENTE (in-app, Stripe Elements/PaymentElement). O
// cliente nunca é levado à página hospedada do Stripe: assina o Pro + add-ons via
// carrinho (1 pagamento), paga faturas avulsas e gerencia os cartões da recorrência,
// tudo dentro do app.

interface PlanIncl {
  numbers: number; projects: number; messages: number;
  storage_mb: number; retention_days: number; unlimited_messages: boolean;
}
interface Pricing {
  plans?: { free: PlanIncl; pro: PlanIncl };
  currencies: Record<string, { pro_month_cents: number }>;
}
interface Subscription {
  plan?: string; status?: string; renews_at?: string; cancel_at?: string;
  recurring?: boolean; currency?: string; grace_until?: string;
}

const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const BILLING_URL = `${APP_ORIGIN}/billing`;

export function Billing() {
  const { locale, t } = useI18n();
  const qc = useQueryClient();
  const { data: me } = useMe(true);
  const email = me?.email ?? '';
  const [busy, setBusy] = useState('');
  const [checkout, setCheckout] = useState<CheckoutTarget | null>(null);
  const [saveCard, setSaveCard] = useState(true); // didático: por padrão sugere recorrência

  const ent = useQuery({ queryKey: ['entitlements'], queryFn: async (): Promise<Entitlements | null> => {
    const r = await apiFetch('GET', '/me/entitlements'); return r.ok ? (r.data as Entitlements) : null;
  } });
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: async (): Promise<Pricing | null> => {
    const r = await apiFetch('GET', '/pricing'); return r.ok ? (r.data as Pricing) : null;
  } });
  const sub = useQuery({ queryKey: ['subscription'], queryFn: async (): Promise<Subscription | null> => {
    const r = await apiFetch('GET', '/me/subscription'); return (r.ok ? r.data : null) as Subscription | null;
  } });
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: async (): Promise<Invoice[]> => {
    const r = await apiFetch('GET', '/me/invoices'); return ((r.ok ? r.data : { data: [] }) as { data: Invoice[] }).data ?? [];
  } });
  const cards = useQuery({ queryKey: ['pms'], queryFn: async (): Promise<PaymentMethod[]> => {
    const r = await apiFetch('GET', '/billing/payment-methods'); return ((r.ok ? r.data : { data: [] }) as { data: PaymentMethod[] }).data ?? [];
  } });
  const cartQ = useQuery({ queryKey: ['addon-cart'], queryFn: async (): Promise<Cart> => {
    const r = await apiFetch('GET', '/me/addons/cart'); return (r.ok ? r.data : CART_EMPTY) as Cart;
  } });
  const cart = cartQ.data ?? CART_EMPTY;

  const e = ent.data;
  const ccy = e?.currency ?? 'BRL';
  const money = (cents: number) => formatMoney(cents, ccy);
  const refresh = () => {
    ['entitlements', 'subscription', 'invoices', 'addon-cart', 'pms'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  // Carrinho de add-ons: edita (server) e atualiza na hora.
  const [cartBusy, setCartBusy] = useState('');
  const cartChange = async (kind: string, delta: number) => {
    setCartBusy(`${kind}:${delta > 0 ? 'add' : 'rm'}`);
    try {
      const r = await apiFetch('POST', '/me/addons', { kind, delta });
      if (r.ok) { qc.setQueryData(['addon-cart'], r.data); if (delta < 0) qc.invalidateQueries({ queryKey: ['entitlements'] }); }
      else { const code = (r.data as { code?: string } | undefined)?.code; toast.error(code === 'not_pro' ? t('billing.errNotPro') : t('billing.errGeneric')); }
    } finally { setCartBusy(''); }
  };

  // Pagar o carrinho: abre o CHECKOUT TRANSPARENTE com o client_secret do backend.
  const payCart = async (saveCardOverride?: boolean) => {
    setCartBusy('pay');
    try {
      const r = await apiFetch('POST', '/me/addons/cart/checkout', { save_card: saveCardOverride ?? saveCard });
      const cs = (r.data as { client_secret?: string } | undefined)?.client_secret;
      const invId = (r.data as { invoice_id?: string } | undefined)?.invoice_id;
      if (!r.ok || !cs) {
        if (r.status === 501) toast.error(t('billing.notConfigured'));
        else toast.error(t('billing.checkoutFailed'));
        return;
      }
      setCheckout({
        clientSecret: cs,
        mode: 'payment',
        returnUrl: `${BILLING_URL}?paid=${invId ?? ''}`,
        showSaveToggle: true,
        saveCardDefault: saveCard,
        title: t('billing.payCartTitle'),
        description: t('billing.payCartDesc'),
        confirmLabel: t('billing.checkoutPay', { amount: money(cart.prorated_cents) }),
      });
    } finally { setCartBusy(''); }
  };

  const clearCart = async () => {
    setCartBusy('clear');
    try {
      const r = await apiFetch('DELETE', '/me/addons/cart');
      if (r.ok) { qc.setQueryData(['addon-cart'], r.data ?? CART_EMPTY); toast.success(t('billing.cartCleared')); }
    } finally { setCartBusy(''); }
  };

  // Bloqueia sair da tela com contratações pendentes (carrinho não vazio).
  const blocker = useBlocker(() => !cart.empty);
  useEffect(() => {
    const warn = (ev: BeforeUnloadEvent) => { if (!cart.empty) { ev.preventDefault(); ev.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [cart.empty]);

  // Retorno do checkout transparente (?paid=<invoice_id>) — métodos com redirect
  // (Pix/boleto) e fallback de cartão. Mostra sucesso e refaz as queries.
  const [autoPayId, setAutoPayId] = useState<string | undefined>();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('paid')) {
      toast.success(t('billing.checkoutSuccess'));
      window.history.replaceState({}, '', '/billing');
      refresh();
    }
    const pay = params.get('pay');
    if (pay) { setAutoPayId(pay); window.history.replaceState({}, '', '/billing'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Assinar Pro = adiciona o Pro ao CARRINHO (pendente). Não cobra/ativa: o cliente
  // pode somar add-ons e paga tudo de uma vez no checkout do carrinho.
  const subscribe = async () => {
    setBusy('subscribe');
    track('subscribe_started', {});
    try {
      const r = await apiFetch('POST', '/me/plan/upgrade');
      if (r.status === 501) { toast.error(t('billing.notConfigured')); return; }
      if (!r.ok) { toast.error(t('billing.errProCart')); return; }
      qc.setQueryData(['addon-cart'], r.data);
      toast.success(t('billing.proAddedCart'));
    } finally { setBusy(''); }
  };

  // Cancelar (fim do ciclo) ou reativar o Pro.
  const toggleCancel = async (cancel: boolean) => {
    setBusy(cancel ? 'cancel' : 'reactivate');
    try {
      const r = await apiFetch('POST', cancel ? '/me/plan/cancel' : '/me/plan/uncancel');
      if (r.ok) { toast.success(cancel ? t('billing.cancelScheduled') : t('billing.reactivated')); refresh(); }
      else if (r.status === 501) toast.error(t('billing.notConfigured'));
      else toast.error(t('billing.errGeneric'));
    } finally { setBusy(''); }
  };

  if (ent.isLoading || !e || !pricing.data) return <Skeleton className="h-96 w-full" />;
  const p = pricing.data;
  const proCents = p.currencies[ccy]?.pro_month_cents ?? e.plan_monthly_cents;
  const exempt = e.plan === 'exempt';
  const isPro = e.plan === 'pro';
  const s = sub.data;
  const recurrenceOn = !!(e.recurring ?? s?.recurring);

  const storage = (mb: number) => (mb >= 1024 ? t('billing.storageGb', { value: (mb / 1024).toLocaleString(locale, { maximumFractionDigits: 1 }) }) : t('billing.storageMb', { value: String(mb) }));
  const features = (pl?: PlanIncl) => pl ? [
    t('billing.featNumbers', { n: String(pl.numbers) }),
    pl.unlimited_messages ? t('billing.featMessagesUnlimited') : t('billing.featMessages', { n: pl.messages.toLocaleString(locale) }),
    t('billing.featProjects', { n: String(pl.projects) }),
    storage(pl.storage_mb),
    t('billing.featRetention', { n: String(pl.retention_days) }),
  ] : [];

  return (
    <div className="flex flex-col gap-6">
      {/* Banner: contratações pendentes de pagamento (carrinho não vazio) */}
      {!cart.empty && (
        <div className="flex flex-col gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-amber-900 sm:flex-row sm:items-center">
          <ShoppingCart className="size-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{t('billing.pendingTitle')}</p>
            <p className="text-sm text-amber-800">
              {cart.plan_pro
                ? t('billing.pendingProBody', { amount: money(cart.prorated_cents) })
                : t('billing.pendingAddonBody', { amount: money(cart.prorated_cents) })}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="ghost" className="text-amber-900 hover:text-destructive" disabled={!!cartBusy} onClick={clearCart}>
              {cartBusy === 'clear' ? <Loader2 className="size-4 animate-spin" /> : null} {t('common.clear')}
            </Button>
            <Button size="sm" disabled={!!cartBusy} onClick={() => payCart()}>
              {cartBusy === 'pay' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.checkoutPay', { amount: money(cart.prorated_cents) })}
            </Button>
          </div>
        </div>
      )}

      {/* Consumo da franquia do Free, logo acima dos planos: a decisão de upgrade vem
          junto do número real. Sem link próprio — o card do Pro está logo abaixo. */}
      <FreeQuotaGauge showUpgrade={false} />

      {exempt ? (
        <Card className="border-2 border-emerald-500/40">
          <CardHeader className="flex-row items-center gap-2">
            <Sparkles className="size-5 text-emerald-600" /><CardTitle>{t('billing.courtesyPlan')}</CardTitle><Badge variant="success">{t('billing.active')}</Badge>
          </CardHeader>
          <CardContent><p className="text-sm text-muted-foreground">{t('billing.courtesyBody2')}</p></CardContent>
        </Card>
      ) : (
        // Dois planos lado a lado; o contratado em destaque.
        <div className="grid gap-4 md:grid-cols-2">
          <PlanCard
            name={t('billing.planFree')} highlighted={!isPro}
            price={t('billing.priceFree')} features={features(p.plans?.free)}
            action={isPro ? undefined : <Badge variant="secondary">{t('billing.currentPlan')}</Badge>}
          />
          <PlanCard
            name={t('billing.planPro')} highlighted={isPro}
            price={t('billing.priceMonth', { price: money(proCents) })} features={features(p.plans?.pro)}
            note={isPro && e.plan_cancel_at ? t('billing.dropsToFree', { date: new Date(e.plan_cancel_at).toLocaleDateString(locale) })
              : isPro && e.plan_renews_at ? t('billing.renewsOn', { date: new Date(e.plan_renews_at).toLocaleDateString(locale) }) : undefined}
            badge={isPro ? (e.plan_cancel_at ? <Badge variant="secondary">{t('billing.cancelsEndCycle')}</Badge> : <Badge variant="success">{t('billing.active')}</Badge>)
              : cart.plan_pro ? <Badge variant="secondary">{t('billing.inCart')}</Badge> : undefined}
            action={
              !isPro ? (
                cart.plan_pro ? (
                  <Button variant="outline" className="w-full" disabled={!!cartBusy} onClick={() => payCart()}>
                    {cartBusy === 'pay' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.checkoutPay', { amount: money(cart.prorated_cents) })}
                  </Button>
                ) : (
                  <Button className="w-full" disabled={busy === 'subscribe'} onClick={subscribe}>
                    {busy === 'subscribe' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.subscribePro')}
                  </Button>
                )
              ) : e.plan_cancel_at ? (
                <Button variant="outline" className="w-full" disabled={busy === 'reactivate'} onClick={() => toggleCancel(false)}>
                  {busy === 'reactivate' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.reactivate')}
                </Button>
              ) : (
                <Button variant="ghost" className="w-full text-muted-foreground hover:text-destructive" disabled={busy === 'cancel'} onClick={() => toggleCancel(true)}>
                  {busy === 'cancel' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.cancelPlan')}
                </Button>
              )
            }
          />
        </div>
      )}

      {/* Loja de add-ons — visível no Pro OU com Pro no carrinho */}
      {(isPro || cart.plan_pro) && (
        <AddonStore e={e} cart={cart} money={money} locale={locale} busy={cartBusy} onChange={cartChange} onPay={() => payCart()} canPay={!cart.empty} />
      )}

      {/* Cartões + status da recorrência */}
      {!exempt && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div className="flex items-center gap-2"><CreditCard className="size-5 text-primary" /><CardTitle>{t('billing.paymentTitle')}</CardTitle></div>
            {isPro && (recurrenceOn
              ? <Badge variant="success" className="gap-1"><RefreshCw className="size-3" /> {t('billing.recurrenceOn')}</Badge>
              : <Badge variant="secondary">{t('billing.recurrenceOff')}</Badge>)}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {isPro && (
              <p className="text-sm text-muted-foreground">
                {recurrenceOn ? t('billing.recurrenceOnBody') : t('billing.recurrenceOffBody')}
              </p>
            )}
            <SavedCards cards={cards.data ?? []} email={email} returnUrl={BILLING_URL} onChanged={() => qc.invalidateQueries({ queryKey: ['pms'] })} />
          </CardContent>
        </Card>
      )}

      {/* Faturas e histórico de pagamento */}
      {!exempt && (
        <Card>
          <CardHeader className="flex-row items-center gap-2"><FileText className="size-5 text-primary" /><CardTitle>{t('billing.invoicesTitle')}</CardTitle></CardHeader>
          <CardContent>
            <InvoiceList
              invoices={invoices.data ?? []}
              locale={locale}
              returnUrl={BILLING_URL}
              autoPayId={autoPayId}
              onChanged={refresh}
            />
          </CardContent>
        </Card>
      )}

      {/* Checkout transparente do carrinho — oferece os cartões já salvos. */}
      <CheckoutDialog
        target={checkout}
        savedCards={cards.data ?? []}
        onClose={() => setCheckout(null)}
        onSaveCardChange={setSaveCard}
        onSuccess={() => { setCheckout(null); toast.success(t('billing.checkoutSuccess')); refresh(); }}
      />

      {/* Bloqueio ao sair com carrinho pendente */}
      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-card p-6 shadow-xl">
            <div className="flex items-center gap-2"><ShoppingCart className="size-5 text-amber-600" /><h3 className="text-lg font-semibold">{t('billing.leaveTitle')}</h3></div>
            <p className="text-sm text-muted-foreground">{t('billing.leaveBody', { amount: money(cart.prorated_cents) })}</p>
            <div className="flex flex-col gap-2">
              <Button disabled={!!cartBusy} onClick={() => payCart()}>{cartBusy === 'pay' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.checkoutPay', { amount: money(cart.prorated_cents) })}</Button>
              <Button variant="outline" disabled={!!cartBusy} onClick={async () => { await clearCart(); blocker.proceed?.(); }}>
                {cartBusy === 'clear' ? <Loader2 className="size-4 animate-spin" /> : null} {t('billing.leaveClear')}
              </Button>
              <Button variant="ghost" onClick={() => blocker.reset?.()}>{t('billing.leaveStay')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Card de plano (Free | Pro), com destaque quando é o contratado.
function PlanCard({ name, price, features, highlighted, badge, note, action }: {
  name: string; price: string; features: string[]; highlighted?: boolean;
  badge?: React.ReactNode; note?: string; action?: React.ReactNode;
}) {
  return (
    <Card className={highlighted ? 'border-2 border-primary shadow-amber' : 'border'}>
      <CardHeader className="flex-row items-center gap-2">
        <Sparkles className={`size-5 ${highlighted ? 'text-primary' : 'text-muted-foreground'}`} />
        <CardTitle>{name}</CardTitle>
        {badge}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-end gap-2">
          <span className={`font-display text-3xl font-extrabold tabular-nums ${highlighted ? 'text-primary' : ''}`}>{price}</span>
        </div>
        <ul className="flex flex-col gap-1.5 text-sm text-muted-foreground">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-2"><Check className="size-4 shrink-0 text-emerald-600" /> {f}</li>
          ))}
        </ul>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
        {action}
      </CardContent>
    </Card>
  );
}
